import { authorizedFetch } from '../utils';
import { getElasticTimeZoneOffset } from '../reporting-utils';
import { getQuery, resolveDependencies } from './report-renderer';
import { assignElasticResultsToRow } from './terms-report';
import { getGroupProperty } from '../flows/flow-report-utils';

const getTotalRow = async (
  totalRow,
  measures,
  queryEventUrl,
  query,
  addError
) => {
  return authorizedFetch(queryEventUrl, 'POST', query)
    .then(response => {
      if (query.aggs && !response.aggregations) {
        throw new Error('No Aggregations found');
      }

      if (response.aggregations) {
        assignElasticResultsToRow(measures, totalRow, response.aggregations);
      }
      return totalRow;
    })
    .catch(e => {
      if (e.name !== 'AbortError') {
        if (e.message) {
          addError('Unable to get report data', JSON.stringify(e.message));
        }
        if (
          e.error?.caused_by?.type === 'too_many_buckets_exception' ||
          e.error?.root_cause.some(
            cause => cause.type === 'too_many_buckets_exception'
          )
        ) {
          addError(
            'Unable to get report data',
            'Report generated too many row buckets. Try a different configuration.'
          );
        }
      }
    });
};

const getCompositeData = async ({ queryEventUrl, query, addError, signal }) => {
  let compositeData = [];
  let response = await authorizedFetch(queryEventUrl, 'POST', query, signal);
  let afterKey = response.aggregations.reporting.after_key;
  compositeData.push(...response.aggregations.reporting.buckets);

  try {
    while (afterKey) {
      query.aggs.reporting.composite.after = afterKey;
      response = await authorizedFetch(queryEventUrl, 'POST', query, signal);
      compositeData.push(...response.aggregations.reporting.buckets);
      afterKey = response.aggregations.reporting.after_key;
    }

    return compositeData;
  } catch (e) {
    addError('Unable to get report data', JSON.stringify(e.message));
  }
};

const getCompositeFlattenedRows = (buckets, mappedGroupFields, measures) => {
  let rows = [];

  //flatten data
  buckets.forEach(b => {
    let row = {};
    let keyParts = [];
    // get keys of bucket fields
    const bucketKeys = Object.keys(b).filter(k => k !== 'doc_count');
    // for each bucket field, flatten the data and add to total row
    bucketKeys.forEach(k => {
      // if key field, add each key field value to key parts
      if (k === 'key') {
        const groupKeys = Object.keys(b[k]);
        groupKeys.forEach(gk => {
          if (gk === 'timeStamp' || gk === 'timeStampHourly') {
            const trimmedTimeStamp = b[k][gk].split('.')[0];

            keyParts.push(trimmedTimeStamp);
            row[gk] = b[k][gk];
          } else {
            if (b[k][gk] === 'All' || b[k][gk] === 'all') {
              keyParts.push(`'${b[k][gk]}'`);
              row[gk] = `'${b[k][gk]}'`;
            } else {
              keyParts.push(b[k][gk] ?? 'No value');
              row[gk] = b[k][gk] ?? 'No value';
            }
          }
        });
      } else {
        assignElasticResultsToRow(measures, row, b);
      }
    });

    row.key = keyParts.join('.');
    rows.push(row);
  });

  // returns row values concatenated\joined via '.'
  const getGroup = (row, groupIndex) => {
    return mappedGroupFields
      .slice(0, groupIndex + 1)
      .map(g => row[g])
      .join('.');
  };

  // creates a subtotal row
  const getSubTotalRow = (row, groupIndex) => {
    let subTotalRow = {
      isSubtotalRow: true,
      rows: []
    };

    //group values...
    mappedGroupFields.forEach((mgf, mgfi) => {
      subTotalRow[mgf] = row[mgf];
      if (mgfi > groupIndex) {
        subTotalRow[mgf] = 'All';
      }
    });

    subTotalRow.key = getSubTotalKey(subTotalRow);

    return subTotalRow;
  };

  const getSubTotalKey = row => {
    const groupKeyParts = [];
    // build subTotal key from the field values of each group field selected
    mappedGroupFields.forEach(f => {
      if (f === 'timeStamp') {
        groupKeyParts.push(row.timeStamp.split('.')[0]);
      } else groupKeyParts.push(row[f]);
    });

    const subTotalKey = groupKeyParts.join('.');
    return subTotalKey;
  };

  if (rows.length > 0 && mappedGroupFields.length > 0) {
    let tempRows = rows;

    // Dont need to group the last field. ElasticSearch already does that.
    // only doing subtotals for first (n-1) groups
    mappedGroupFields
      .slice(0, mappedGroupFields.length - 1)
      .forEach((group, groupIndex) => {
        let oldGroup = getGroup(rows[0], groupIndex);
        let subTotalRow = getSubTotalRow(rows[0], groupIndex);
        let data = tempRows;
        tempRows = [];
        data.forEach(row => {
          // if it's a total row add to temp and continue on
          if (row.hasOwnProperty('isSubtotalRow')) {
            tempRows.push(row);
          } else {
            // control break
            let newGroup = getGroup(row, groupIndex);
            if (newGroup !== oldGroup) {
              oldGroup = newGroup;
              tempRows.push(subTotalRow);
              subTotalRow = getSubTotalRow(row, groupIndex);
            }

            //map fields for total rows
            Object.keys(row).forEach(f => {
              if (!mappedGroupFields.includes(f)) {
                if (!subTotalRow.hasOwnProperty(f)) {
                  subTotalRow[f] = row[f];
                } else if (!isNaN(row[f])) {
                  subTotalRow[f] += Number(row[f]);
                }
              }
            });

            subTotalRow.rows.push(row); //maybe?
            tempRows.push(row);
          }
        });

        tempRows.push(subTotalRow);
      });

    rows = tempRows;
  }
  return rows;
};

export const getCompositeCDSData = async (
  groups,
  fields,
  termsFilters,
  range,
  interval,
  isPreview,
  queryEventUrl,
  setCompositeReportData,
  setLoading,
  addError,
  paramCache,
  signal,
  additionalMusts,
  additionalMustNots
) => {
  setLoading(true);
  const compositeFields = fields.map(f => f.aggs.filter);
  // get the composite query
  const query = getQuery({
    range,
    compositeFields,
    termsFilters,
    additionalMusts,
    additionalMustNots
  });

  //get the total query
  const totalQuery = getQuery({
    range,
    termsFilters,
    additionalMusts,
    additionalMustNots
  });

  const previewFilter = {
    prefix: { scope: 'preview_' }
  };

  // if preview is toggled, add preview scope to composite query and total query
  if (isPreview) {
    query.query.bool.must.push(previewFilter);
    totalQuery.query.bool.must.push(previewFilter);
  } else {
    query.query.bool.must_not.push(previewFilter);
    totalQuery.query.bool.must_not.push(previewFilter);
  }

  let measureAggs;
  // build aggregations based on fields (metrics) selected
  let fieldsToGrab = fields.map(f => ({
    depth: 0,
    field: f
  }));

  if (fields.length > 0) {
    measureAggs = {};
    // add the fields from the requiredProperties of derived metrics
    await resolveDependencies(fields, measureAggs, paramCache, fieldsToGrab);

    //sort based on dependency depth
    fieldsToGrab.sort((a, b) => b.depth - a.depth);

    //remove duplicates
    fieldsToGrab = fieldsToGrab.reduce((acc, curr) => {
      if (!acc.find(f => f.property === curr.field.property)) {
        acc.push(curr.field);
      }
      return acc;
    }, []);
  }

  const groupProperties = groups;
  const cdsDataRequests = [];

  if (groupProperties.length > 0) {
    let groupSources;
    // if there are groups selected, build composite sources based on groups selected
    groupSources = groupProperties.map(g => {
      let groupAgg;

      if (g === 'timeStamp' || g === 'timeStampHourly') {
        groupAgg = {
          [g]: {
            date_histogram: {
              field: 'timeStamp',
              fixed_interval: interval,
              time_zone: getElasticTimeZoneOffset(),
              format: 'iso8601'
            }
          }
        };
      } else if (g === 'properties.campaignId') {
        // Partner campaign events carry their id in properties.partnerCampaignId
        // (not properties.campaignId), so they would otherwise collapse into the
        // missing bucket. Coalesce both ids so performance and partner campaigns
        // group/resolve in the same "Campaign" column.
        const groupKey = getGroupProperty(g);
        groupAgg = {
          [groupKey]: {
            terms: {
              script: {
                // properties.campaignId is keyword-mapped, but properties.partnerCampaignId
                // is text and only its .keyword subfield has doc values.
                source:
                  "if (doc.containsKey('properties.campaignId') && doc['properties.campaignId'].size() > 0) { return doc['properties.campaignId'].value; } else if (doc.containsKey('properties.partnerCampaignId.keyword') && doc['properties.partnerCampaignId.keyword'].size() > 0) { return doc['properties.partnerCampaignId.keyword'].value; } else { return null; }"
              },
              value_type: 'string',
              missing_bucket: true
            }
          }
        };
      } else {
        const groupKey = getGroupProperty(g);
        groupAgg = {
          [groupKey]: {
            terms: {
              field: g,
              missing_bucket: true
            }
          }
        };
      }
      return groupAgg;
    });

    query.aggs = {
      reporting: {
        aggs: { ...measureAggs },
        composite: {
          size: 5000,
          sources: groupSources
        }
      }
    };

    cdsDataRequests.push(
      getCompositeData({
        queryEventUrl,
        query,
        addError,
        signal
      })
    );
  }
  totalQuery.aggs = measureAggs;

  const measures = Array.from(fieldsToGrab);
  const mappedGroupFields = groupProperties.map(gp => getGroupProperty(gp));

  // create the total row
  const totalRow = { key: 'totalRow' };
  mappedGroupFields.forEach(gf => {
    totalRow[gf] = 'All';
  });

  const rows = [totalRow];

  cdsDataRequests.push(
    getTotalRow(totalRow, measures, queryEventUrl, totalQuery, addError)
  );

  // once all promises are done, flatten rows and set state
  await Promise.allSettled(cdsDataRequests).then(res => {
    if (res.every(r => r.value !== undefined)) {
      const compositeData = res.find(r => Array.isArray(r.value))?.value;
      if (compositeData && compositeData.length > 0) {
        rows.push(
          ...getCompositeFlattenedRows(
            compositeData,
            mappedGroupFields,
            measures
          )
        );
      }

      setCompositeReportData(rows);
      setLoading(false);
    }
  });
};
